import {
  AdminInput,
  Button,
  Disclaimer,
  Div,
  ErrorToast,
  H5,
  Modal,
  OutstandingItem,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { SERVICE_LABELS } from '../helpers/receivablesLabels';
import { ReceivablesPasswordField } from './ReceivablesPasswordField';

const NOTE_MAX = 1000;

export type ResolveMode = 'paid' | 'writeOff';

type Props = {
  /** The item being closed; null keeps the modal shut. */
  item: OutstandingItem | null;
  mode: ResolveMode;
  busy: boolean;
  onClose: () => void;
  onConfirm: (note: string, adminPassword: string) => Promise<boolean>;
};

const COPY: Record<
  ResolveMode,
  { title: string; hint: string; placeholder: string; button: string }
> = {
  paid: {
    title: 'Mark as paid',
    hint: 'Paid outside the system. Say how, so the next person can find it.',
    placeholder: 'e.g. Paid by check #1234 on Oct 2',
    button: 'Mark as paid',
  },
  writeOff: {
    title: 'Write off',
    hint: 'It will not be collected. Say why.',
    placeholder: 'e.g. Owner closed the business, agreed with finance',
    button: 'Write off',
  },
};

/**
 * Closing an open item by hand: paid outside the system, or written off. Both
 * want a note - it is the only record of what happened - and the receivables
 * password.
 */
export const ResolveItemModal = ({
  item,
  mode,
  busy,
  onClose,
  onConfirm,
}: Props) => {
  const [note, setNote] = useState('');
  const [password, setPassword] = useState('');
  const copy = COPY[mode];
  const repaid = item?.repaidAmount ?? 0;

  // A note belongs to the item it was written for.
  useEffect(() => {
    if (!item) {
      setNote('');
      setPassword('');
    }
  }, [item]);

  const submit = async () => {
    const trimmed = note.trim();
    if (!trimmed) return ErrorToast('Please add a note');
    if (trimmed.length > NOTE_MAX)
      return ErrorToast(`The note can't be longer than ${NOTE_MAX} characters`);
    if (!password) return ErrorToast('Please enter the admin password');

    const ok = await onConfirm(trimmed, password);
    if (ok) onClose();
  };

  return (
    <Modal
      isOpen={!!item}
      closeModal={onClose}
      logo={false}
      header={copy.title}
    >
      {item && (
        <Div $flex $column $gap="16px">
          <Div $box $p="12px 16px">
            {/* Partly repaid by a plan: what closing it settles is the rest,
                so that leads, as on the row. */}
            <H5 $m="0">
              {displayCentsInDollars(item.amount - repaid)} -{' '}
              {SERVICE_LABELS[item.service]}
            </H5>
            {repaid > 0 && (
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                of {displayCentsInDollars(item.amount)},{' '}
                <span style={{ color: lightTheme.green }}>
                  {displayCentsInDollars(repaid)} repaid by a plan
                </span>
              </Disclaimer>
            )}
            <Disclaimer $m="0">{item.description}</Disclaimer>
          </Div>

          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            {copy.hint}
          </Disclaimer>

          <AdminInput
            type="textarea"
            label="Note"
            placeholder={copy.placeholder}
            value={note}
            maxLength={NOTE_MAX}
            $textareaHeight={120}
            onChange={(event) => setNote(event.target.value)}
          />

          <ReceivablesPasswordField value={password} onChange={setPassword} />

          <Button
            $selected
            $w="100%"
            $loading={busy}
            disabled={busy}
            onClick={submit}
          >
            {copy.button}
          </Button>
          <Button $selected $gray $w="100%" onClick={onClose}>
            Cancel
          </Button>
        </Div>
      )}
    </Modal>
  );
};
